/**
 * Hook useTurmas
 * Gerencia operações CRUD de turmas.
 * Multitenant: usa perfil /me para restringir listagem ao tenant (nucleo_id in tenant nucleos).
 */

import { useState, useCallback } from "react";
import { turmasService, nucleosService } from "../services";
import { useAuth } from "./useAuth";

const DIA_VALIDO_MAP = {
  SEGUNDA: "SEGUNDA",
  TERCA: "TERCA",
  QUARTA: "QUARTA",
  QUINTA: "QUINTA",
  SEXTA: "SEXTA",
  SABADO: "SABADO",
  DOMINGO: "DOMINGO",
};

const normalizeDiaValor = valor => {
  if (!valor) return null;
  const base = String(valor)
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return DIA_VALIDO_MAP[base] || null;
};

const normalizeDiasSemana = dias => {
  if (!dias) return [];
  const lista = Array.isArray(dias) ? dias : [dias];
  return Array.from(new Set(lista.map(dia => normalizeDiaValor(dia)).filter(Boolean)));
};

const getIdFromHabilidade = habilidade => {
  if (!habilidade) return null;
  if (typeof habilidade === "string") return habilidade;
  return (
    habilidade.id || habilidade.value || habilidade.habilidade_id || habilidade.habilidadeId || null
  );
};

const normalizeTurmaResponse = data => {
  if (!data) return null;

  const diasSemanaNormalizados = normalizeDiasSemana(
    data.diasSemana || data.dias_semana || data.diaSemana || data.dia_semana || []
  );

  // Processar ofertas (pode ser JSON string com habilidades ou string simples com modalidade)
  let habilidades = [];
  let modalidadeId = null;

  if (data.ofertas) {
    try {
      // Tentar fazer parse como JSON (para habilidades - array)
      const ofertasParsed = JSON.parse(data.ofertas);
      if (Array.isArray(ofertasParsed)) {
        habilidades = ofertasParsed;
      } else {
        // Se não for array, pode ser um ID único (modalidade)
        modalidadeId = String(ofertasParsed).trim();
      }
    } catch (e) {
      // Se não for JSON válido, tratar como string simples (modalidade ID)
      modalidadeId = String(data.ofertas).trim();
    }
  }

  // Se já vier habilidades do backend, usar essas
  const rawHabilidades =
    data.habilidades ||
    data.habilidades_ids ||
    data.habilidadesId ||
    data.habilidades_id ||
    habilidades;

  if (Array.isArray(rawHabilidades) && rawHabilidades.length > 0) {
    habilidades = rawHabilidades.map(item => getIdFromHabilidade(item)).filter(Boolean);
  }

  // Se já vier modalidadeId do backend, usar esse
  if (!modalidadeId) {
    modalidadeId = data.modalidade_id || data.modalidadeId || data.modalidade?.id || null;
  }

  // Preservar objetos sala e nucleo do backend
  // O backend retorna sala como objeto ou campos individuais sala_*
  let sala = null;
  if (data.sala && typeof data.sala === "object") {
    // Se já vem como objeto do backend, preservar
    sala = data.sala;
  } else if (data.sala_id || data.sala_identificador) {
    // Construir objeto a partir de campos individuais
    sala = {
      id: data.sala_id || data.sala?.id || data.sala?.sala_id,
      sala_id: data.sala_id || data.sala?.sala_id,
      identificador:
        data.sala_identificador || data.sala?.identificador || data.sala?.nome || "Sem Sala",
      nome: data.sala_identificador || data.sala?.identificador || data.sala?.nome || "Sem Sala",
      turno: data.sala_turno || data.sala?.turno,
      // Buscar capacidade da sala: primeiro dos campos da view (sala_cap_nominal, sala_cap_maxima), depois da sala relacionada, depois fallback
      cap_nominal: data.sala_cap_nominal ?? data.sala?.cap_nominal ?? data.capacidade_sala ?? null,
      cap_maxima: data.sala_cap_maxima ?? data.sala?.cap_maxima ?? data.capacidade_sala ?? null,
      status: data.sala_status || data.sala?.status,
      nucleo_id: data.sala?.nucleo_id || data.nucleo_id,
    };
  }

  // O backend retorna nucleo como objeto ou campos individuais nucleo_*
  // O nucleo_id pode vir diretamente da turma ou da sala
  let nucleo = null;
  const nucleoId = data.nucleo_id || data.nucleo?.id || data.nucleo?.nucleo_id || sala?.nucleo_id;

  if (data.nucleo && typeof data.nucleo === "object") {
    // Se já vem como objeto do backend, preservar
    nucleo = data.nucleo;
  } else if (nucleoId || data.nucleo_nome) {
    // Construir objeto a partir de campos individuais
    // Cria objeto nucleo mesmo se apenas nucleo_nome estiver disponível
    nucleo = {
      id: nucleoId || null,
      nucleo_id: nucleoId || null,
      nome: data.nucleo_nome || data.nucleo?.nome || "Sem Núcleo",
      tipo: data.nucleo_tipo || data.nucleo?.tipo,
      endereco: data.nucleo_endereco || data.nucleo?.endereco,
    };
  }

  // Processar horarios_config (JSONB do PostgreSQL)
  let horariosConfig = null;
  if (data.horarios_config) {
    try {
      // Se já for objeto, usar diretamente
      if (typeof data.horarios_config === 'object' && !Array.isArray(data.horarios_config)) {
        horariosConfig = data.horarios_config;
      } else if (typeof data.horarios_config === 'string') {
        // Se for string JSON, fazer parse
        horariosConfig = JSON.parse(data.horarios_config);
      }
    } catch (e) {
      console.warn('Erro ao processar horarios_config:', e);
      horariosConfig = null;
    }
  }

  // Construir objeto final, garantindo que sala e nucleo sejam preservados
  const result = {
    ...data,
    id: data.id || data.turma_id || data.turmaId,
    turmaId: data.turma_id || data.turmaId || data.id,
    nome: data.nome || null,
    salaId: data.sala_id || data.salaId || sala?.id || sala?.sala_id || null,
    nucleoId: data.nucleo_id || data.nucleoId || nucleo?.id || nucleo?.nucleo_id || null,
    turno: data.turno || null, // Turno da turma (matutino/vespertino)
    modalidadeId,
    diasSemana: diasSemanaNormalizados,
    diaSemana: data.diaSemana || data.dia_semana || diasSemanaNormalizados[0] || null,
    horario: data.horario || data.horarioInicio || "",
    horarioInicio: data.horario || data.horarioInicio || "",
    horarioFim: data.horario_fim || data.horarioFim || "",
    horarios_config: horariosConfig, // Nova estrutura de múltiplos horários
    habilidades,
    modalidade: modalidadeId ? { id: modalidadeId } : data.modalidade || null,
    monitor_id: data.monitor_id || data.monitorId || null,
    monitorResponsavel:
      data.monitor_nome || data.monitorNome || data.monitor_id || data.monitorId || null,
    // Lotação atual (total de alunos vinculados)
    lotacaoAtual: data.total_alunos ?? data.totalAlunos ?? data.lotacaoAtual ?? data.lotacao_atual ?? 0,
    totalAlunos: data.total_alunos ?? data.totalAlunos ?? data.lotacaoAtual ?? data.lotacao_atual ?? 0,
  };

  // Garantir que sala e nucleo sejam sempre definidos (mesmo que null)
  // Isso sobrescreve qualquer valor que possa ter vindo do spread
  result.sala = sala;
  result.nucleo = nucleo;

  return result;
};

const mapTurmasArray = payload => {
  if (!payload) return [];
  return payload.map(item => normalizeTurmaResponse(item)).filter(Boolean);
};

/**
 * Valida estrutura de horarios_config (validação client-side opcional)
 * @param {Object} horariosConfig - Configuração de horários
 * @returns {{ valido: boolean, erros: string[] }}
 */
const validarHorariosTurma = (horariosConfig) => {
  const erros = [];

  if (!horariosConfig || typeof horariosConfig !== 'object') {
    return { valido: true, erros: [] }; // NULL é válido (campo opcional)
  }

  const diasValidos = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO', 'DOMINGO'];

  // Validar cada dia da semana
  for (const dia of diasValidos) {
    if (horariosConfig[dia]) {
      const horarios = horariosConfig[dia];

      if (!Array.isArray(horarios)) {
        erros.push(`Dia ${dia}: deve ser um array de horários`);
        continue;
      }

      if (horarios.length > 3) {
        erros.push(`Dia ${dia}: máximo de 3 horários por dia`);
      }

      // Validar cada horário
      horarios.forEach((horario, index) => {
        if (typeof horario !== 'object' || horario === null) {
          erros.push(`Dia ${dia}, horário ${index + 1}: deve ser um objeto`);
          return;
        }

        // Campos obrigatórios
        if (typeof horario.ordem !== 'number' || horario.ordem < 1) {
          erros.push(`Dia ${dia}, horário ${index + 1}: ordem deve ser um número >= 1`);
        }

        if (!horario.horario || typeof horario.horario !== 'string') {
          erros.push(`Dia ${dia}, horário ${index + 1}: horario é obrigatório`);
        }

        if (!horario.horario_fim || typeof horario.horario_fim !== 'string') {
          erros.push(`Dia ${dia}, horário ${index + 1}: horario_fim é obrigatório`);
        }

        // Validar formato de horário (HH:MM:SS ou HH:MM)
        const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
        if (horario.horario && !timeRegex.test(horario.horario)) {
          erros.push(`Dia ${dia}, horário ${index + 1}: formato de horario inválido (use HH:MM ou HH:MM:SS)`);
        }

        if (horario.horario_fim && !timeRegex.test(horario.horario_fim)) {
          erros.push(`Dia ${dia}, horário ${index + 1}: formato de horario_fim inválido (use HH:MM ou HH:MM:SS)`);
        }

        // Validar que horário fim é depois do início
        if (horario.horario && horario.horario_fim) {
          const inicio = new Date(`2000-01-01T${horario.horario}`);
          const fim = new Date(`2000-01-01T${horario.horario_fim}`);
          if (fim <= inicio) {
            erros.push(`Dia ${dia}, horário ${index + 1}: horario_fim deve ser posterior a horario`);
          }
        }
      });

      // Validar sobreposição de horários no mesmo dia
      for (let i = 0; i < horarios.length; i++) {
        for (let j = i + 1; j < horarios.length; j++) {
          const h1 = horarios[i];
          const h2 = horarios[j];

          if (h1.horario && h1.horario_fim && h2.horario && h2.horario_fim) {
            const h1Inicio = new Date(`2000-01-01T${h1.horario}`);
            const h1Fim = new Date(`2000-01-01T${h1.horario_fim}`);
            const h2Inicio = new Date(`2000-01-01T${h2.horario}`);
            const h2Fim = new Date(`2000-01-01T${h2.horario_fim}`);

            // Verificar sobreposição: h1 sobrepõe h2 se h1_inicio < h2_fim AND h1_fim > h2_inicio
            if (h1Inicio < h2Fim && h1Fim > h2Inicio) {
              erros.push(
                `Dia ${dia}: horários ${i + 1} (${h1.horario} - ${h1.horario_fim}) e ${j + 1} (${h2.horario} - ${h2.horario_fim}) se sobrepõem`
              );
            }
          }
        }
      }
    }
  }

  return {
    valido: erros.length === 0,
    erros,
  };
};

const buildTurmaPayload = turmaData => {
  if (!turmaData) return {};

  const payload = { ...turmaData };

  if (payload.salaId !== undefined) {
    payload.sala_id = payload.salaId;
    delete payload.salaId;
  }

  // NÃO converter nucleoId para nucleo_id
  // A tabela turma não tem coluna nucleo_id - o núcleo vem através da sala_id (sala.nucleo_id)
  if (payload.nucleoId !== undefined) {
    delete payload.nucleoId;
  }

  // Converter dias da semana para formato do backend
  // A constraint turma_dias_semana_valid espera MAIÚSCULAS: SEGUNDA, TERCA, etc.
  if (payload.diasSemana) {
    const diasNormalizados = normalizeDiasSemana(payload.diasSemana);
    // Converter para MAIÚSCULAS (formato esperado pela constraint)
    payload.dias_semana = diasNormalizados
      .map(dia => {
        if (!dia) return null;
        // Garantir que está em MAIÚSCULAS
        return dia.toUpperCase();
      })
      .filter(Boolean);
    delete payload.diasSemana;
  }

  if (payload.diaSemana) {
    // Mantém compatibilidade mas prioriza dias_semana
    if (!payload.dias_semana || payload.dias_semana.length === 0) {
      const diaNormalizado = normalizeDiaValor(payload.diaSemana);
      if (diaNormalizado) {
        // Converter para MAIÚSCULAS (formato esperado pela constraint)
        payload.dias_semana = [diaNormalizado.toUpperCase()];
      }
    }
    delete payload.diaSemana;
  }

  // Converter horarioInicio para horario (o backend espera horario no formato HH:MM)
  if (payload.horarioInicio) {
    // Garantir formato HH:MM
    const horario = String(payload.horarioInicio).trim();
    if (horario && horario.match(/^\d{2}:\d{2}$/)) {
      payload.horario = horario;
    } else {
      // Tentar converter de outros formatos
      payload.horario = horario;
    }
    delete payload.horarioInicio;
  }

  // Converter horarioFim para horario_fim (o backend espera horario_fim no formato HH:MM)
  if (payload.horarioFim) {
    // Garantir formato HH:MM
    const horarioFim = String(payload.horarioFim).trim();
    if (horarioFim && horarioFim.match(/^\d{2}:\d{2}$/)) {
      payload.horario_fim = horarioFim;
    } else {
      // Tentar converter de outros formatos
      payload.horario_fim = horarioFim;
    }
    delete payload.horarioFim;
  }

  // Converter pilar para formato do backend (Pedagógico ou Esportivo)
  if (payload.pilar) {
    const pilarUpper = String(payload.pilar).toUpperCase();
    if (pilarUpper === "PEDAGOGICO" || pilarUpper === "PEDAGÓGICO") {
      payload.pilar = "Pedagógico";
    } else if (pilarUpper === "ESPORTIVO") {
      payload.pilar = "Esportivo";
    }
  }

  // Remover monitor_id da turma se horarios_config existe
  // A vinculação do monitor agora é feita apenas através de horarios_config
  // Se não houver horarios_config, manter compatibilidade com formato antigo
  if (payload.horarios_config && Object.keys(payload.horarios_config).length > 0) {
    // Se tem horarios_config, não salvar monitor_id diretamente na turma
    if (payload.monitorResponsavel !== undefined) {
      delete payload.monitorResponsavel;
    }
    if (payload.monitorId !== undefined) {
      delete payload.monitorId;
    }
    if (payload.monitor_id !== undefined) {
      delete payload.monitor_id;
    }
  } else {
    // Compatibilidade com formato antigo: se não tem horarios_config, usar monitor_id da turma
    if (payload.monitorResponsavel !== undefined) {
      const monitorId = payload.monitorResponsavel;
      if (monitorId && monitorId !== "" && monitorId !== null) {
        payload.monitor_id = monitorId;
      }
      delete payload.monitorResponsavel;
    }

    if (payload.monitorId !== undefined) {
      const monitorId = payload.monitorId;
      if (monitorId && monitorId !== "" && monitorId !== null) {
        payload.monitor_id = monitorId;
      }
      delete payload.monitorId;
    }
  }

  // Processar ofertas (pode vir como array ou já como JSON string)
  if (payload.ofertas !== undefined) {
    if (Array.isArray(payload.ofertas)) {
      // Se for array, converter para JSON string
      payload.ofertas = payload.ofertas.length > 0 ? JSON.stringify(payload.ofertas) : null;
    } else if (typeof payload.ofertas === "string" && payload.ofertas.trim() !== "") {
      // Se já for string, manter (pode ser JSON string ou string simples)
      payload.ofertas = payload.ofertas.trim();
    } else {
      // Se for null, undefined ou string vazia, enviar null
      payload.ofertas = null;
    }
  }

  // Processar horarios_config (JSONB)
  if (payload.horarios_config !== undefined) {
    if (payload.horarios_config === null || payload.horarios_config === '') {
      // Se for null ou string vazia, enviar null
      payload.horarios_config = null;
    } else if (typeof payload.horarios_config === 'object') {
      // Se já for objeto, manter (será serializado como JSON pelo PostgREST)
      // Garantir que está no formato correto
      if (Object.keys(payload.horarios_config).length === 0) {
        payload.horarios_config = null;
      }
    } else if (typeof payload.horarios_config === 'string') {
      // Se for string JSON, tentar fazer parse e validar
      try {
        const parsed = JSON.parse(payload.horarios_config);
        if (typeof parsed === 'object' && parsed !== null) {
          payload.horarios_config = parsed;
        } else {
          payload.horarios_config = null;
        }
      } catch (e) {
        console.warn('Erro ao fazer parse de horarios_config:', e);
        payload.horarios_config = null;
      }
    }
  }

  // Remover campos que não são esperados pelo backend
  if (payload.observacoes !== undefined) {
    delete payload.observacoes;
  }

  // Remover nucleoId se existir (não é usado diretamente, vem da sala)
  if (payload.nucleoId !== undefined) {
    delete payload.nucleoId;
  }

  return payload;
};

/**
 * Hook para gerenciar turmas
 */
export const useTurmas = () => {
  const { user } = useAuth();
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Busca todas as turmas
   */
  const fetchTurmas = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);

    try {
      // Construir filtros para PostgREST
      const postgrestFilters = {};

      if (filters.nucleoId || filters.nucleo_id) {
        postgrestFilters.nucleo_id = `eq.${filters.nucleoId || filters.nucleo_id}`;
      }
      if (filters.salaId || filters.sala_id) {
        postgrestFilters.sala_id = `eq.${filters.salaId || filters.sala_id}`;
      }
      // Multitenant: quando não há filtro por núcleo/sala, restringir ao tenant (perfil /me)
      if (!postgrestFilters.nucleo_id && !postgrestFilters.sala_id && user?.role !== "TECNICO" && user?.negocio_id) {
        const res = await nucleosService.list({ negocioId: user.negocio_id, limit: 5000, includeCount: false });
        const lista = Array.isArray(res) ? res : (res?.data ?? []);
        const nucleoIds = lista.map((n) => n.nucleo_id || n.id).filter(Boolean);
        if (nucleoIds.length > 0) {
          postgrestFilters.nucleo_id = `in.(${nucleoIds.join(",")})`;
        }
      }
      // Nota: Filtro por pilar foi removido - a coluna pilar não existe mais na tabela turma
      // O pilar agora é determinado pelo tipo do núcleo (nucleo.tipo)
      // Se precisar filtrar por pilar, deve ser feito client-side após buscar as turmas
      if (filters.diaSemana || filters.dia_semana) {
        const dia = filters.diaSemana || filters.dia_semana;
        const diaNormalizado = normalizeDiaValor(dia);
        if (diaNormalizado) {
          // Converter para formato do backend (ex: "Segunda")
          const diaBackend = diaNormalizado.charAt(0) + diaNormalizado.slice(1).toLowerCase();
          postgrestFilters.dia_semana = `eq.${diaBackend}`;
        }
      }

      // Por padrão, filtrar apenas turmas ativas (a menos que explicitamente solicitado o contrário)
      if (filters.ativa !== false) {
        postgrestFilters.ativa = `eq.true`;
      }

      // Usa serviço ao invés de apiGet diretamente
      const result = await turmasService.list({
        filters: postgrestFilters,
        order: "nome.asc",
        limit: filters.limit,
        offset: filters.offset || (filters.page && filters.limit ? (filters.page - 1) * filters.limit : undefined),
        includeCount: filters.includeCount !== undefined ? filters.includeCount : false,
      });

      // Extrai data e total se includeCount for true
      const data = result && typeof result === 'object' && 'data' in result ? result.data : result;
      const total = result && typeof result === 'object' && 'total' in result ? result.total : (Array.isArray(data) ? data.length : 0);

      const normalized = mapTurmasArray(Array.isArray(data) ? data : [data]);
      setTurmas(normalized);

      // Retorna total se solicitado
      if (filters.includeCount) {
        return { turmas: normalized, total };
      }
      return normalized;
    } catch (err) {
      const errorMessage = err.message || "Erro ao buscar turmas";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.negocio_id]);

  /**
   * Busca uma turma específica por ID
   */
  const fetchTurmaById = useCallback(async id => {
    setLoading(true);
    setError(null);

    try {
      // Normalizar o ID (pode vir como objeto ou string)
      const normalizedId =
        typeof id === "object" && id !== null ? id.id || id.turma_id || id.turmaId : id;

      if (!normalizedId) {
        throw new Error("ID da turma não fornecido");
      }

      // Usa serviço ao invés de apiGet diretamente
      const data = await turmasService.getById(normalizedId);
      if (!data) {
        throw new Error("Turma não encontrada");
      }
      return normalizeTurmaResponse(data);
    } catch (err) {
      const errorMessage = err.message || "Erro ao buscar turma";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cria uma nova turma
   */
  const createTurma = useCallback(async turmaData => {
    setLoading(true);
    setError(null);

    try {
      const payload = buildTurmaPayload(turmaData);
      // Usa serviço ao invés de apiPost diretamente
      const data = await turmasService.create(payload);

      // Atualiza a lista local
      const normalized = normalizeTurmaResponse(data);
      if (normalized) {
        setTurmas(prev => [...prev, normalized]);
      }

      return normalized;
    } catch (err) {
      const errorMessage = err.message || "Erro ao criar turma";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Atualiza uma turma existente
   */
  const updateTurma = useCallback(async (id, turmaData) => {
    setLoading(true);
    setError(null);

    try {
      const payload = buildTurmaPayload(turmaData);
      // Usa serviço ao invés de apiPut diretamente
      const data = await turmasService.update(id, payload);
      const normalized = normalizeTurmaResponse(data);

      // Atualiza a lista local
      setTurmas(prev =>
        prev.map(turma =>
          (turma.id === id || turma.turmaId === id) && normalized ? normalized : turma
        )
      );

      return normalized;
    } catch (err) {
      const errorMessage = err.message || "Erro ao atualizar turma";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Deleta uma turma
   */
  const deleteTurma = useCallback(async id => {
    setLoading(true);
    setError(null);

    try {
      // Normalizar o ID (pode vir como objeto ou string)
      const normalizedId =
        typeof id === "object" && id !== null ? id.id || id.turma_id || id.turmaId : id;

      if (!normalizedId) {
        const errorMsg = "ID da turma inválido";
        console.error("[useTurmas.deleteTurma] ID inválido:", id);
        setError(errorMsg);
        throw new Error(errorMsg);
      }

      // Usa serviço ao invés de apiDelete diretamente
      await turmasService.delete(normalizedId);

      // Atualiza a lista local
      setTurmas(prev =>
        prev.filter(turma => {
          const turmaId = turma.id || turma.turmaId || turma.turma_id;
          return String(turmaId) !== String(normalizedId);
        })
      );

      return true;
    } catch (err) {
      console.error("[useTurmas.deleteTurma] Erro ao deletar turma:", {
        id,
        error: err,
        message: err?.message,
        status: err?.status,
        data: err?.data,
        response: err?.response
      });

      // Extrair mensagem de erro mais específica
      let errorMessage = "Erro ao deletar turma";

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.detail) {
        errorMessage = err.data.detail;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      // Mensagens específicas por status HTTP
      const status = err?.status || err?.response?.status;
      if (status === 403) {
        errorMessage = "Você não tem permissão para excluir esta turma. Verifique se você tem acesso ao núcleo desta turma.";
      } else if (status === 404) {
        errorMessage = "Turma não encontrada";
      } else if (status === 500) {
        errorMessage = "Erro interno do servidor. A turma pode possuir vínculos que impedem a exclusão.";
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Busca alunos vinculados a uma turma
   */
  const fetchAlunosVinculados = useCallback(async turmaId => {
    setLoading(true);
    setError(null);

    try {
      // Usa serviço ao invés de apiGet diretamente
      const data = await turmasService.getAlunosVinculados(turmaId);
      // O backend retorna array ou objeto com alunos
      if (data && data.alunos) {
        return data.alunos;
      }
      return Array.isArray(data) ? data : [];
    } catch (err) {
      const errorMessage = err.message || "Erro ao buscar alunos vinculados";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Busca estatísticas de uma turma (lotação, frequência média)
   */
  const fetchEstatisticas = useCallback(async turmaId => {
    setLoading(true);
    setError(null);

    try {
      // Usa serviço com RPC function para estatísticas
      const data = await turmasService.getEstatisticas(turmaId);
      return (
        data || {
          lotacaoAtual: 0,
          capacidade: 0,
          frequenciaMedia: 0,
          totalAlunos: 0,
        }
      );
    } catch (err) {
      const errorMessage = err.message || "Erro ao buscar estatísticas";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Busca turmas por sala
   */
  const fetchTurmasBySala = useCallback(async salaId => {
    setLoading(true);
    setError(null);

    try {
      // Usa serviço com filtro de sala
      const data = await turmasService.list({
        filters: { sala_id: `eq.${salaId}` },
      });
      const normalized = mapTurmasArray(Array.isArray(data) ? data : [data]);
      return normalized;
    } catch (err) {
      const errorMessage = err.message || "Erro ao buscar turmas da sala";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Busca turmas do dia para o monitor logado
   * @param {Date|string} data - Data para buscar turmas (padrão: hoje)
   * @returns {Promise<Array>} Lista de turmas do dia
   */
  const getTurmasDoDia = useCallback(async (data = new Date()) => {
    setLoading(true);
    setError(null);

    try {
      const dataParam =
        data instanceof Date
          ? data.toISOString().split("T")[0]
          : data || new Date().toISOString().split("T")[0];

      // Usa serviço com filtro de data
      const turmas = await turmasService.getByDate(dataParam);

      // Normaliza as turmas para garantir que sempre tenham 'id' e 'turma_id'
      const turmasNormalizadas = Array.isArray(turmas)
        ? turmas.map(turma => ({
          ...turma,
          id: turma.id || turma.turma_id,
          turma_id: turma.turma_id || turma.id,
        }))
        : [];

      return turmasNormalizadas;
    } catch (err) {
      const errorMessage = err.message || "Erro ao buscar turmas do dia";
      setError(errorMessage);
      console.error("Erro ao buscar turmas do dia:", err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    turmas,
    loading,
    error,
    fetchTurmas,
    fetchTurmaById,
    createTurma,
    updateTurma,
    deleteTurma,
    fetchAlunosVinculados,
    fetchEstatisticas,
    fetchTurmasBySala,
    getTurmasDoDia,
  };
};

export default useTurmas;
